import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { t, LANGS } from '../i18n';
import { toast } from 'sonner';
import { Shield, ArrowRight, Loader2 } from 'lucide-react';

const DEMO_ACCOUNTS = [
  { label: 'Employee · Policy', email: 'priya.policy@origami.dev', password: 'user123', testid: 'demo-user-policy' },
  { label: 'Dept Head · Policy', email: 'arjun.policy@origami.dev', password: 'head123', testid: 'demo-dept_head-policy' },
  { label: 'Employee · HR', email: 'arjun@sterlite.com', password: 'user123', testid: 'demo-user-hr' },
  { label: 'Employee · Procurement', email: 'rohan@sterlite.com', password: 'user123', testid: 'demo-user-proc' },
  { label: 'Employee · Finance', email: 'ananya@sterlite.com', password: 'user123', testid: 'demo-user-fin' },
  { label: 'Dept Head · HR', email: 'head.hr@sterlite.com', password: 'head123', testid: 'demo-dept_head-hr' },
  { label: 'Dept Head · Procurement', email: 'head.proc@sterlite.com', password: 'head123', testid: 'demo-dept_head-proc' },
  { label: 'Dept Head · Finance', email: 'head.fin@sterlite.com', password: 'head123', testid: 'demo-dept_head-fin' },
  { label: 'Super Admin', email: 'admin@vedanta.com', password: 'admin123', testid: 'demo-super_admin' },
];

export default function LoginPage() {
  const { auth, setAuth, lang, setLanguage } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoBusy, setSsoBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/sso/config`)
      .then(r => setSsoEnabled(!!r.data?.enabled))
      .catch(() => setSsoEnabled(false));
  }, []);

  const ssoLogin = async () => {
    setSsoBusy(true);
    try {
      const { data } = await axios.get(`${API}/auth/sso/login-url`, {
        params: { redirect_uri: `${window.location.origin}/sso/callback` },
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Microsoft SSO unavailable');
      setSsoBusy(false);
    }
  };

  // Already-authenticated users land on their role's home page.
  // We deliberately IGNORE any `location.state.from` here — different roles
  // have different home pages, so blindly returning to the previous URL
  // (e.g. /documents) would send a Super Admin to the wrong screen after
  // a user-role logout/login swap.
  useEffect(() => {
    if (auth) nav('/', { replace: true });
  }, [auth, nav]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      setAuth(data);
      toast.success('Signed in');
      nav('/', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  const quickFill = (acct) => {
    setEmail(acct.email);
    setPassword(acct.password);
  };

  return (
    <div className="min-h-screen grain flex" data-testid="login-page">
      {/* Left - hero */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1660496247667-3fb697c396af?crop=entropy&cs=srgb&fm=jpg&w=1400)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#FAFAF7] via-[#FAFAF7]/60 to-transparent" />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-[#B36A41] pulse-dot rounded-full" />
            <span className="data-label">Vedanta Group</span>
          </div>
          <div>
            <div className="data-label mb-3 accent-line text-[#8E5230]">Enterprise Document Intelligence</div>
            <h1 className="font-headings text-5xl leading-tight" style={{ color: '#1A1A1F' }}>
              Cited answers,<br />
              <span className="text-[#B36A41] italic">audited cost,</span><br />
              from your own documents.
            </h1>
            <p className="mt-6 max-w-md leading-relaxed" style={{ color: '#4A4A55' }}>
              A 6-layer cost router that resolves the same questions from cached answers and structured metadata — and falls back to premium AI only as a last resort.
            </p>
            <div className="flex items-center gap-6 mt-8">
              <div>
                <div className="font-mono text-2xl" style={{ color: '#1A1A1F' }}>₹0.00</div>
                <div className="data-label">Free routes</div>
              </div>
              <div className="w-px h-10" style={{ background: 'rgba(26,26,31,0.12)' }} />
              <div>
                <div className="font-mono text-2xl" style={{ color: '#1A1A1F' }}>₹1.04</div>
                <div className="data-label">Premium ceiling</div>
              </div>
              <div className="w-px h-10" style={{ background: 'rgba(26,26,31,0.12)' }} />
              <div>
                <div className="font-mono text-2xl" style={{ color: '#047857' }}>{'>'}80%</div>
                <div className="data-label">Target free %</div>
              </div>
            </div>
          </div>
          <div className="data-label" style={{ color: '#8A8A95' }}>
            STERLITE ELECTRIC · POC · CONFIDENTIAL
          </div>
        </div>
      </div>

      {/* Right - form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-end mb-8">
            <div className="flex items-center gap-1 border border-white/10 rounded-sm overflow-hidden">
              {LANGS.map(L => (
                <button
                  key={L.code}
                  data-testid={`login-lang-${L.code}`}
                  onClick={() => setLanguage(L.code)}
                  className={`px-3 py-1 text-[11px] font-mono uppercase ${lang === L.code ? 'bg-[#C17F59] text-white' : 'text-slate-400 hover:text-white'}`}
                >
                  {L.label}
                </button>
              ))}
            </div>
          </div>

          <div className="data-label mb-3 accent-line text-[#DDA079]">{t(lang, 'appName')}</div>
          <h2 className="font-headings text-3xl text-white mb-1">{t(lang, 'signIn')}</h2>
          <div className="data-label mb-8">{t(lang, 'company')}</div>

          {ssoEnabled && !showEmail ? (
            <>
              <button
                type="button"
                data-testid="sso-microsoft-btn"
                onClick={ssoLogin}
                disabled={ssoBusy}
                className="btn-primary w-full flex items-center justify-center gap-2.5"
              >
                {ssoBusy ? <Loader2 size={15} className="animate-spin" /> : (
                  <svg width="15" height="15" viewBox="0 0 21 21" aria-hidden="true">
                    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                  </svg>
                )}
                SSO Signin
              </button>
              <p className="text-[12px] text-slate-500 mt-4 text-center">
                Sign in with your organisation&apos;s Microsoft account.
              </p>
              <button
                type="button"
                data-testid="show-email-login"
                onClick={() => setShowEmail(true)}
                className="w-full text-center text-[11px] text-slate-500 hover:text-slate-300 font-mono uppercase tracking-wider mt-8 transition-colors"
              >
                Use email &amp; password instead
              </button>
            </>
          ) : (
          <>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="data-label block mb-2">{t(lang, 'email')}</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@sterlite.com"
                className="v-input"
                autoComplete="email"
                required
              />
            </div>
            <div>
              <label className="data-label block mb-2">{t(lang, 'password')}</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                className="v-input"
                autoComplete="current-password"
                required
              />
            </div>
            <button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="btn-primary w-full flex items-center justify-center gap-2 mt-6"
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
              {busy ? '…' : t(lang, 'signIn')}
            </button>
          </form>

          {ssoEnabled && (
            <button
              type="button"
              data-testid="back-to-sso"
              onClick={() => setShowEmail(false)}
              className="w-full text-center text-[11px] text-slate-500 hover:text-slate-300 font-mono uppercase tracking-wider mt-5 transition-colors"
            >
              ← Back to SSO Signin
            </button>
          )}

          <div className="mt-10 pt-6 border-t border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <Shield size={11} className="text-[#C17F59]" />
              <span className="data-label">POC Demo Accounts</span>
            </div>
            <div className="space-y-1.5">
              {DEMO_ACCOUNTS.map(a => (
                <button
                  key={a.email}
                  type="button"
                  data-testid={a.testid}
                  onClick={() => quickFill(a)}
                  className="w-full flex items-center justify-between px-3 py-2 border border-white/5 hover:border-white/15 hover:bg-white/[0.02] transition-colors text-left rounded-sm"
                >
                  <div>
                    <div className="text-[12px] text-white">{a.label}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{a.email}</div>
                  </div>
                  <span className="text-[10px] text-slate-500 font-mono">click to fill</span>
                </button>
              ))}
            </div>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
